<script>
import LoadingTemplate from "@/components/widgets/loading-template.vue";
import { cloneDeep } from "lodash";

export default {
  name: "BursListModal",
  components: {
    LoadingTemplate
  },
  data() {
    return {
      tableData: [],
      loading: false,
      rfpData: {},
      totalRows: 1,
      currentPage: 1,
      perPage: 10,
      pageOptions: [10, 25, 50, 100],
      filter: "",
      filterOn: ["serialNumber", "payee", "particulars", "rfpNumber"],
      sortBy: "serialNumber",
      sortDesc: false,
      fields: [
        {
          key: "serialNumber",
          label: "BURS/OBRS No.",
          sortable: true,
          thStyle: { width: "15%" },
        },
        {
          key: "payee",
          label: "Payee",
          sortable: true,
          thStyle: { width: "25%" },
        },
        {
          key: "particulars",
          label: "Particulars",
          sortable: true,
          thStyle: { width: "35%" },
        },
        {
          key: "bursDate",
          label: "BURS Date",
          sortable: true,
          thStyle: { width: "15%" },
        },
        {
          key: "total",
          label: "Amount",
          sortable: true,
          thStyle: { width: "10%" },
        },
      ],
    };
  },
  computed: {
    rows() {
      return this.tableData.length;
    }
  },
  methods: {
    async getData(rfpData) {
      this.rfpData = cloneDeep(rfpData);
      await this.fetchBursList();
      this.$bvModal.show("burs-list-modal");
    },
    async fetchBursList() {
      this.loading = true;
      this.tableData = [];
      
      try {
        await this.$store.dispatch("burs/getPostedBurs", {
          cycle: this.$store.state.data.cycle,
          campusId: this.$store.state.data.selectedCampus.id,
        })
        .then((res) => {
          if (res.data.error) {
            return this.showToast(res.data.errorMessage, "error");
          }
          const filteredData = res.data.filter(item => {
            return !item.isDueAndDemandable && (item.parentBursId === 0 || item.parentBursId === null);
          });
          
          this.tableData = filteredData.map(item => ({
            ...item,
            filterKey: `${item.serialNumber} ${item.payee} ${item.particulars} ${item.rfpNumber}`
          }));
        });
      } catch (err) {
        this.showToast('Something went wrong!', "error");
      }
      
      this.loading = false;
    },
    onFiltered(filteredItems) {
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },
    selectBurs(selectedItems) {
      if (selectedItems && selectedItems.length > 0) {
        this.$emit("drop", selectedItems[0]);
        this.$bvModal.hide("burs-list-modal");
      }
    },
    formatCurrency(amount) {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "PHP",
      }).format(amount);
    },
    formatDate(date) {
      return new Date(date).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    },
  },
};
</script>

<template>
  <b-modal
    id="burs-list-modal"
    title="Select Parent BURS/OBRS"
    size="xl"
    title-class="font-18"
    hide-footer
    no-close-on-backdrop
  >
    <div class="row mt-1 mb-3">
      <div class="col-sm-12 col-md-6">
        <div class="dataTables_filter">
          <label class="d-inline-flex align-items-center">
            Search:
            <b-form-input
              autocomplete="off"
              v-model="filter"
              type="search"
              placeholder="Search BURS..."
              class="form-control form-control-sm ms-2"
            ></b-form-input>
          </label>
        </div>
      </div>
      <div class="col-sm-12 col-md-6">
        <div class="d-flex justify-content-end">
          <label class="d-inline-flex align-items-center">
            Show:
            <b-form-select
              v-model="perPage"
              :options="pageOptions"
              class="form-select form-select-sm ms-2"
              style="width: auto;"
            ></b-form-select>
            entries
          </label>
        </div>
      </div>
    </div>

    <div v-if="loading" class="text-center my-5 py-5">
      <LoadingTemplate class="my-5"/>
    </div>

    <div v-if="!loading && tableData.length === 0" class="text-center my-5 py-5">
      <div class="mb-3">
        <i class="bx bx-info-circle font-size-48 text-muted"></i>
      </div>
      <h5 class="text-muted">No Parent BURS/OBRS Available</h5>
      <p class="text-muted mb-0">
        No posted BURS/OBRS records found that can be used as parent for this RFP.
        <br>
        <small>Only "Not Due and Demandable" BURS/OBRS from the same RFP can be selected as parent.</small>
      </p>
    </div>

    <template v-if="!loading && tableData.length > 0">
      <div class="table-responsive mb-0">
        <b-table
          class="datatables"
          :items="tableData"
          :fields="fields"
          responsive="sm"
          :per-page="perPage"
          :current-page="currentPage"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          :filter="filter"
          :filter-included-fields="filterOn"
          @filtered="onFiltered"
          bordered
          outlined
          hover
          striped
          show-empty
          select-mode="single"
          selectable
          @row-selected="selectBurs"
          selected-variant="success"
        >
          <template #empty="scope">
            <div class="text-center">{{ scope.emptyText }}</div>
          </template>

          <template #cell(serialNumber)="row">
            <div class="d-flex align-items-center">
              <span class="badge badge-soft-info px-2 py-1 font-size-12">
                {{ row.item.serialNumber }}
              </span>
            </div>
          </template>

          <template #cell(payee)="row">
            <div>
              <div class="text-white font-weight-medium">
                {{ row.item.payee }}
              </div>
              <small class="text-muted">{{ row.item.deliveryUnit }}</small>
            </div>
          </template>

          <template #cell(particulars)="row">
            <div class="text-truncate" style="max-width: 300px;" :title="row.item.particulars">
              {{ row.item.particulars }}
            </div>
          </template>

          <template #cell(bursDate)="row">
            <span class="text-muted">
              {{ formatDate(new Date(row.item.bursDate)) }}
            </span>
          </template>

          <template #cell(total)="row">
            <span class="text-success font-weight-bold">
              {{ formatCurrency(row.item.total) }}
            </span>
          </template>
        </b-table>
      </div>

      <div class="row mt-3">
        <div class="col-sm-12 col-md-5">
          <div class="dataTables_info">
            Showing {{ (currentPage - 1) * perPage + 1 }} to 
            {{ Math.min(currentPage * perPage, totalRows) }} of 
            {{ totalRows }} entries
          </div>
        </div>
        <div class="col-sm-12 col-md-7">
          <div class="dataTables_paginate paging_simple_numbers float-end">
            <ul class="pagination pagination-rounded mb-0">
              <b-pagination
                v-model="currentPage"
                :total-rows="totalRows"
                :per-page="perPage"
                size="sm"
              ></b-pagination>
            </ul>
          </div>
        </div>
      </div>
    </template>

    <div class="modal-footer border-top pt-3 mt-3">
      <div class="d-flex justify-content-between w-100">
        <small class="text-muted">
          <i class="bx bx-info-circle me-1"></i>
          Click on a row to select a parent BURS/OBRS
        </small>
        <button 
          type="button" 
          class="btn btn-secondary btn-sm" 
          @click="$bvModal.hide('burs-list-modal')"
        >
          Close
        </button>
      </div>
    </div>
  </b-modal>
</template>

<style lang="scss" scoped>
.table tbody tr {
  cursor: pointer;
}

.table tbody tr:hover {
  background-color: rgba(var(--bs-primary-rgb), 0.1);
}

.badge {
  font-size: 0.75rem;
}

.dataTables_info {
  color: var(--bs-gray-600);
  font-size: 0.875rem;
  margin-top: 0.5rem;
}

.text-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>